// src/components/Admin/FirmPortalClientDetail.js
import React, { useState, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Box, Typography, Paper, CircularProgress, Alert, Chip, Collapse,
    IconButton, Divider, Stack, Avatar, Tabs, Tab, Grid, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    PersonOutline as PersonOutlineIcon,
    SmartToyOutlined as SmartToyIcon,
    Article as ArticleIcon,
    CalendarToday as CalendarIcon,
    Chat as ChatIcon,
    Business as BusinessIcon,
    Language as LanguageIcon,
    Description as DescriptionIcon,
    QuestionAnswer as QuestionAnswerIcon,
    AdminPanelSettings as AdminIcon,
    Send as SendIcon,
    Assessment as ProfileIcon
} from '@mui/icons-material';
import { db, functions } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import BEPMemoDisplay from '../DecisionMemo/BEPMemoDisplay';
import ClientProfileModal from '../ClientProfileModal';
import ExecutiveSummaryModal from './ExecutiveSummaryModal';
import { Summarize as SummarizeIcon } from '@mui/icons-material';

// Extracted component to prevent parent re-renders during typing
const AskQuestionDialog = React.memo(({ open, onClose, client, onSuccess }) => {
    const [questionText, setQuestionText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!questionText.trim()) {
            toast.error('Please enter a question.');
            return;
        }

        setSubmitting(true);
        try {
            const firmPortalAskQuestion = httpsCallable(functions, 'firmPortalAskQuestion', { timeout: 600000 });
            const result = await firmPortalAskQuestion({
                clientId: client.id,
                questionText: questionText.trim()
            });

            if (result.data.success) {
                toast.success(result.data.message || 'Question submitted successfully!');
                setQuestionText('');
                onSuccess();
            } else {
                throw new Error(result.data.error || 'Failed to submit question');
            }
        } catch (err) {
            console.error('Error asking question:', err);
            toast.error(err.message || 'Failed to submit question');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setQuestionText('');
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <QuestionAnswerIcon color="primary" />
                    <span>Ask Question for {client?.name || client?.officialCompanyName}</span>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    This question will be attributed to you and processed through the normal answer pipeline.
                </Alert>
                <TextField
                    autoFocus
                    multiline
                    rows={4}
                    fullWidth
                    label="Question"
                    placeholder="Enter your question for this client..."
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    disabled={submitting}
                    sx={{ mt: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !questionText.trim()}
                    startIcon={submitting ? <CircularProgress size={20} /> : <QuestionAnswerIcon />}
                >
                    {submitting ? 'Submitting...' : 'Submit Question'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

// Extracted component to prevent parent re-renders during typing
const FollowUpDialog = React.memo(({ open, onClose, followUpParent, onSuccess }) => {
    const [followUpText, setFollowUpText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!followUpText.trim() || !followUpParent) return;

        setSubmitting(true);
        try {
            const functionName = followUpParent.type === 'question' 
                ? 'firmPortalFollowUpQuestion' 
                : 'firmPortalFollowUpBusinessUpdate';
            const callable = httpsCallable(functions, functionName, { timeout: 600000 });
            
            const params = followUpParent.type === 'question'
                ? { parentQuestionId: followUpParent.id, followUpText: followUpText.trim(), clientId: followUpParent.clientId }
                : { businessUpdateId: followUpParent.id, followUpText: followUpText.trim(), clientId: followUpParent.clientId };

            const result = await callable(params);

            if (result.data.success) {
                toast.success('Follow-up question submitted and answered!');
                setFollowUpText('');
                onSuccess();
            }
        } catch (error) {
            console.error('Error submitting follow-up:', error);
            toast.error(error.message || 'Failed to submit follow-up question');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setFollowUpText('');
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <ChatIcon color="primary" />
                    <span>Ask Follow-up Question</span>
                </Stack>
                {followUpParent?.item && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Following up on: {followUpParent.type === 'question' 
                            ? followUpParent.item.questionText?.substring(0, 100) 
                            : followUpParent.item.title}
                        {((followUpParent.type === 'question' ? followUpParent.item.questionText : followUpParent.item.title)?.length > 100) && '...'}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Your follow-up question"
                    fullWidth
                    multiline
                    rows={4}
                    value={followUpText}
                    onChange={(e) => setFollowUpText(e.target.value)}
                    disabled={submitting}
                    placeholder="Ask a follow-up question to dive deeper into this topic..."
                />
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!followUpText.trim() || submitting}
                    startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
                >
                    {submitting ? 'Submitting...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

const FirmPortalClientDetail = ({ client, user, orgConfig, onClose }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedItems, setExpandedItems] = useState(new Set());
    
    // Ask Question modal state
    const [askQuestionOpen, setAskQuestionOpen] = useState(false);
    
    // Follow-up state
    const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
    const [followUpParent, setFollowUpParent] = useState(null); // { id, type, clientId, item }
    
    // Decision Memo state
    const [memoGenerating, setMemoGenerating] = useState(null); // ID of item generating memo
    
     // Client Profile state
    const [profileModalOpen, setProfileModalOpen] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileData, setProfileData] = useState(null);

    // Check if user is admin or enterprise user (both can perform actions)
    const isAdmin = user?.firmPortalRole === 'admin';
    const isEnterprise = user?.firmPortalRole === 'admin' || user?.firmPortalRole === 'user';

    // Executive Summary state
    const [execSummaryModalOpen, setExecSummaryModalOpen] = useState(false);

    // Fetch questions and updates for this client
    useEffect(() => {
        if (!client?.id) return;

        setLoading(true);

        // Fetch questions - include organizationId for security rules
        const questionsQuery = query(
            collection(db, 'questions'),
            where('clientId', '==', client.id),
            where('organizationId', '==', user.organizationId),
            orderBy('createdAt', 'desc')
        );

        const unsubQuestions = onSnapshot(questionsQuery, (snapshot) => {
            const questionList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dataType: 'question'
            }));
            setQuestions(questionList);
        });

        // Fetch business updates - include organizationId for security rules
        const updatesQuery = query(
            collection(db, 'businessUpdates'),
            where('clientId', '==', client.id),
            where('organizationId', '==', user.organizationId),
            orderBy('createdAt', 'desc')
        );

        const unsubUpdates = onSnapshot(updatesQuery, (snapshot) => {
            const updateList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dataType: 'update'
            }));
            setUpdates(updateList);
            setLoading(false);
        });

        return () => {
            unsubQuestions();
            unsubUpdates();
        };
    }, [client?.id]);

    // Combine and sort all items, threading follow-ups into parent questions
    const allItems = useMemo(() => {
        // Separate parent questions from follow-ups
        const parentQuestions = questions.filter(q => !q.parentId);
        const followUpQuestions = questions.filter(q => q.parentId);
        
        // Build follow-up map
        const questionFollowUpMap = {};
        followUpQuestions.forEach(followUp => {
            if (!questionFollowUpMap[followUp.parentId]) {
                questionFollowUpMap[followUp.parentId] = [];
            }
            questionFollowUpMap[followUp.parentId].push(followUp);
        });
        
        // Merge follow-ups into parent questions
        const questionsWithFollowUps = parentQuestions.map(question => {
            const embeddedFollowUps = question.followUps || [];
            const mappedFollowUps = questionFollowUpMap[question.id] || [];
            const allFollowUps = [...embeddedFollowUps, ...mappedFollowUps];
            
            // Sort follow-ups by creation date
            allFollowUps.sort((a, b) => {
                const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                return aTime - bTime;
            });
            
            return { ...question, followUps: allFollowUps };
        });
        
        // Combine with updates and sort
        const combined = [...questionsWithFollowUps, ...updates];
        combined.sort((a, b) => 
            (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
        );
        return combined;
    }, [questions, updates]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp?.toDate) return 'Unknown date';
        return timestamp.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Toggle expansion
    const toggleExpansion = (itemId) => {
        setExpandedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    // Handle opening follow-up modal
    const handleOpenFollowUp = (item, type) => {
        setFollowUpParent({ id: item.id, type, clientId: client.id, item });
        setFollowUpModalOpen(true);
    };

    // Handle generating decision memo
    const handleGenerateDecisionMemo = async (item, parentType, signalNumber = null) => {
        const itemId = item.id;
        setMemoGenerating(itemId);
        
        try {
            const generateDecisionMemo = httpsCallable(functions, 'generateDecisionMemo', { timeout: 600000 });
            const contextText = parentType === 'question' 
                ? item.questionText 
                : (item.title || 'SavvaiSignals');
                
            const result = await generateDecisionMemo({
                clientId: client.id,
                parentId: itemId,
                parentType: parentType,
                contextText: contextText,
                signalNumber: signalNumber
            });

            if (result.data.success) {
                toast.success('Decision Memo generated successfully!');
            }
        } catch (error) {
            console.error('Error generating Decision Memo:', error);
            toast.error(error.message || 'Failed to generate Decision Memo');
        } finally {
            setMemoGenerating(null);
        }
    };

    // Handle generating client profile
    const handleGenerateProfile = async () => {
        setProfileLoading(true);
        setProfileModalOpen(true);
        setProfileData(null);
        
        try {
            const generateClientProfile = httpsCallable(functions, 'generateClientProfile', { timeout: 300000 });
            const result = await generateClientProfile({ clientId: client.id });
            
            if (result.data.success) {
                setProfileData(result.data.profile);
                toast.success('Client Profile generated successfully!');
            } else {
                throw new Error(result.data.error || 'Failed to generate profile');
            }
        } catch (error) {
            console.error('Error generating client profile:', error);
            toast.error(error.message || 'Failed to generate profile');
            setProfileModalOpen(false);
        } finally {
            setProfileLoading(false);
        }
    };

    // Render follow-ups
    const renderFollowUps = (followUps) => {
        if (!followUps || followUps.length === 0) return null;

        return (
            <Box sx={{ ml: 1, mt: 2, borderLeft: '3px solid #f0f0f0', pl: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                    <ChatIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                    Follow-up Thread
                </Typography>
                <Stack spacing={2.5}>
                    {followUps.map((followUp, index) => {
                        // Check for BEP Decision Memo
                        if (followUp.type === 'decision_memo' && followUp.framework === 'business_evolution_pyramid') {
                            return (
                                <Box key={followUp.id || index} sx={{ mt: 3, mb: 3 }}>
                                    <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9fa', borderLeft: '4px solid #005DAA' }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#005DAA' }}>
                                            <ArticleIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Decision Memo (Business Evolution Pyramid)
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatTimestamp(followUp.createdAt)}
                                        </Typography>
                                    </Paper>
                                    <BEPMemoDisplay followUpData={followUp} />
                                </Box>
                            );
                        }
                        
                        // Standard Decision Memo
                        if (followUp.type === 'decision_memo') {
                            return (
                                <Box key={followUp.id || index} sx={{ mt: 3, mb: 3 }}>
                                    <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8f9fa', borderLeft: '4px solid #1976d2' }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1976d2' }}>
                                            <ArticleIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Decision Memo
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatTimestamp(followUp.createdAt)}
                                        </Typography>
                                    </Paper>
                                    <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
                                        <Box sx={{ '& p': { mb: 1.5 }, '& ul, & ol': { pl: 3 } }}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {followUp.response || followUp.answer}
                                            </ReactMarkdown>
                                        </Box>
                                    </Paper>
                                </Box>
                            );
                        }
                        
                        // Regular Q&A follow-up
                        return (
                            <Box key={followUp.id || index}>
                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                    <Paper elevation={1} sx={{ p: 1.5, bgcolor: '#f5f5f5', maxWidth: '80%', borderRadius: '16px 16px 0 16px' }}>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {followUp.question || followUp.followUpQuestion}
                                        </Typography>
                                    </Paper>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                        <PersonOutlineIcon fontSize="small" />
                                    </Avatar>
                                </Stack>
                                {followUp.answer && (
                                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                                            <SmartToyIcon fontSize="small" />
                                        </Avatar>
                                        <Paper elevation={1} sx={{ p: 1.5, maxWidth: '80%', borderRadius: '16px 16px 16px 0' }}>
                                            <Box sx={{ '& p': { mb: 0.5, fontSize: '0.875rem' }, '& ul, & ol': { pl: 2.5, fontSize: '0.875rem' } }}>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {followUp.answer}
                                                </ReactMarkdown>
                                            </Box>
                                        </Paper>
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        );
    };

    // Render question card
    const renderQuestionCard = (question) => {
        const isExpanded = expandedItems.has(question.id);
        const hasFollowUps = question.followUps && question.followUps.length > 0;
        const isAdminQuestion = question.askedViaFirmPortal === true;

        return (
            <Paper key={question.id} elevation={2} sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', border: '1px solid', borderColor: 'primary.light' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                            <Chip icon={<QuestionAnswerIcon />} label="Question" size="small" color="primary" variant="filled" />
                            {hasFollowUps && (
                                <Chip icon={<ChatIcon />} label={`${question.followUps.length} follow-up(s)`} size="small" color="info" variant="outlined" />
                            )}
                            {isAdminQuestion && (
                                <Chip 
                                    icon={<AdminIcon />} 
                                    label={`Asked by ${question.askedByAdminName || 'Admin'}`} 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined" 
                                />
                            )}
                        </Stack>
                        <Typography variant="body1" fontWeight={600} color="primary.dark" sx={{ cursor: 'pointer' }} onClick={() => toggleExpansion(question.id)}>
                            {question.questionText}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <CalendarIcon sx={{ fontSize: 14 }} />
                            {formatTimestamp(question.createdAt)}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => toggleExpansion(question.id)} size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                    <Divider sx={{ my: 2 }} />
                    {question.answer && (
                        <Box mb={2}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Answer:</Typography>
                            <Paper elevation={0} variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Box sx={{ '& p': { mb: 1 }, '& ul, & ol': { pl: 3 } }}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {question.answer}
                                    </ReactMarkdown>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                    {hasFollowUps && renderFollowUps(question.followUps)}
                    
                     {/* Actions for Q&A - Available to all enterprise users */}
                    {isEnterprise && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ChatIcon />}
                                onClick={() => handleOpenFollowUp(question, 'question')}
                            >
                                Ask Follow-up
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={memoGenerating === question.id ? <CircularProgress size={16} /> : <ArticleIcon />}
                                onClick={() => handleGenerateDecisionMemo(question, 'question')}
                                disabled={memoGenerating === question.id}
                            >
                                {memoGenerating === question.id ? 'Generating...' : 'Decision Memo'}
                            </Button>
                        </Box>
                    )}
                </Collapse>
            </Paper>
        );
    };

    // Render business update card
    const renderUpdateCard = (update) => {
        const isExpanded = expandedItems.has(update.id);
        const hasFollowUps = update.followUps && update.followUps.length > 0;

        return (
            <Paper key={update.id} elevation={2} sx={{ mb: 2, p: 2, bgcolor: '#f3e5f5', border: '1px solid', borderColor: 'secondary.light' }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Chip icon={<ArticleIcon />} label="SavvaiSignals" size="small" color="secondary" variant="filled" />
                            {hasFollowUps && (
                                <Chip icon={<ChatIcon />} label={`${update.followUps.length} follow-up(s)`} size="small" color="info" variant="outlined" />
                            )}
                        </Stack>
                        <Typography variant="body1" fontWeight={600} color="secondary.dark" sx={{ cursor: 'pointer' }} onClick={() => toggleExpansion(update.id)}>
                            {update.title || 'SavvaiSignals'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                            <CalendarIcon sx={{ fontSize: 14 }} />
                            {formatTimestamp(update.createdAt)}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => toggleExpansion(update.id)} size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                    <Divider sx={{ my: 2 }} />
                    <Box mb={2}>
                        <Paper elevation={0} variant="outlined" sx={{ p: 2, bgcolor: 'white' }}>
                            <Box sx={{ '& p': { mb: 1 }, '& ul, & ol': { pl: 3 }, '& h1, & h2, & h3': { mt: 1.5, mb: 1 } }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {update.content || 'No content available.'}
                                </ReactMarkdown>
                            </Box>
                        </Paper>
                    </Box>
                    {hasFollowUps && renderFollowUps(update.followUps)}
                    
                    {/* Actions for SavvaiSignals - Available to all enterprise users */}
                    {isEnterprise && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ChatIcon />}
                                onClick={() => handleOpenFollowUp(update, 'business_update')}
                            >
                                Ask Follow-up
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={memoGenerating === update.id ? <CircularProgress size={16} /> : <ArticleIcon />}
                                onClick={() => handleGenerateDecisionMemo(update, 'business_update')}
                                disabled={memoGenerating === update.id}
                            >
                                {memoGenerating === update.id ? 'Generating...' : 'Decision Memo'}
                            </Button>
                        </Box>
                    )}
                </Collapse>
            </Paper>
        );
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Client Info Header */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <BusinessIcon color="action" />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Company</Typography>
                                <Typography variant="body2" fontWeight={500}>
                                    {client.officialCompanyName || client.name}
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    {client.companyWebsite && (
                        <Grid item xs={12} md={3}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <LanguageIcon color="action" />
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Website</Typography>
                                    <Typography variant="body2">
                                        <a href={client.companyWebsite} target="_blank" rel="noopener noreferrer">
                                            {client.companyWebsite}
                                        </a>
                                    </Typography>
                                </Box>
                            </Stack>
                        </Grid>
                    )}
                    <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <DescriptionIcon color="action" />
                            <Box>
                                <Typography variant="caption" color="text.secondary">Activity</Typography>
                                <Typography variant="body2">
                                    {questions.length} questions, {updates.length} signals
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                     {/* Action Buttons - Available to all enterprise users */}
                    {isEnterprise && (
    <Grid item xs={12} md={3}>
        <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} flexWrap="wrap">
            <Button
                variant="outlined"
                color="primary"
                startIcon={profileLoading ? <CircularProgress size={20} /> : <ProfileIcon />}
                onClick={handleGenerateProfile}
                disabled={profileLoading}
            >
                {profileLoading ? 'Generating...' : 'Client Profile'}
            </Button>
            <Button
                variant="outlined"
                color="secondary"
                startIcon={<SummarizeIcon />}
                onClick={() => setExecSummaryModalOpen(true)}
            >
                Executive Summary
            </Button>
            <Button
                variant="contained"
                color="primary"
                startIcon={<QuestionAnswerIcon />}
                onClick={() => setAskQuestionOpen(true)}
            >
                Ask Question
            </Button>
        </Box>
    </Grid>
)}
                </Grid>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label={`All Activity (${allItems.length})`} />
                    <Tab label={`Questions (${questions.length})`} />
                    <Tab label={`SavvaiSignals (${updates.length})`} />
                </Tabs>
            </Box>

            {/* Content */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                {activeTab === 0 && (
                    allItems.length === 0 ? (
                        <Alert severity="info">No activity yet for this client.</Alert>
                    ) : (
                        allItems.map(item => 
                            item.dataType === 'question' 
                                ? renderQuestionCard(item) 
                                : renderUpdateCard(item)
                        )
                    )
                )}
                {activeTab === 1 && (
                    questions.length === 0 ? (
                        <Alert severity="info">No questions yet for this client.</Alert>
                    ) : (
                        questions.map(q => renderQuestionCard(q))
                    )
                )}
                {activeTab === 2 && (
                    updates.length === 0 ? (
                        <Alert severity="info">No SavvaiSignals yet for this client.</Alert>
                    ) : (
                        updates.map(u => renderUpdateCard(u))
                    )
                )}
            </Box>

            {/* Ask Question Modal */}
            <AskQuestionDialog
                open={askQuestionOpen}
                onClose={() => setAskQuestionOpen(false)}
                client={client}
                onSuccess={() => setAskQuestionOpen(false)}
            />

            {/* Follow-up Question Modal */}
            <FollowUpDialog
                open={followUpModalOpen}
                onClose={() => {
                    setFollowUpModalOpen(false);
                    setFollowUpParent(null);
                }}
                followUpParent={followUpParent}
                onSuccess={() => {
                    setFollowUpModalOpen(false);
                    setFollowUpParent(null);
                }}
            />

            {/* Client Profile Modal */}
            <ClientProfileModal
                open={profileModalOpen}
                onClose={() => setProfileModalOpen(false)}
                profileData={profileData}
                isLoading={profileLoading}
            />

            {/* Executive Summary Modal */}
            <ExecutiveSummaryModal
                open={execSummaryModalOpen}
                onClose={() => setExecSummaryModalOpen(false)}
                client={client}
              user={user}
            />

        </Box>
    );
};

export default FirmPortalClientDetail;